import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Globe, Copy, RefreshCw, Save, ExternalLink, Code2, Eye, EyeOff,
} from "lucide-react";

const copyText = (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Enlace copiado");
  } catch {
    toast.error("No se pudo copiar");
  }
};

function PageSlotCard({ page, onRefresh }) {
  const [name, setName] = useState(page.name || "");
  const [html, setHtml] = useState(page.html || "");
  const [isActive, setIsActive] = useState(page.is_active !== false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showHtml, setShowHtml] = useState(false);

  useEffect(() => {
    setName(page.name || "");
    setHtml(page.html || "");
    setIsActive(page.is_active !== false);
    setDirty(false);
  }, [page.id, page.updated_at]);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const publicUrl = `${origin}/p/${page.slug}`;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/public-pages/admin/${page.key}`,
        { name, html, is_active: isActive },
        { withCredentials: true });
      toast.success("Cambios guardados");
      setDirty(false);
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const rotateSlug = async () => {
    if (!window.confirm("¿Generar un nuevo enlace? El enlace anterior dejará de funcionar inmediatamente.")) return;
    setRotating(true);
    try {
      await axios.post(`${API}/public-pages/admin/${page.key}/rotate-slug`, {}, { withCredentials: true });
      toast.success("Nuevo enlace generado");
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al regenerar");
    } finally {
      setRotating(false);
    }
  };

  return (
    <Card className="overflow-hidden" data-testid={`public-page-${page.key}`}>
      <CardHeader className="bg-slate-50 border-b">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex-1">
            <CardTitle className="flex items-center gap-2 text-base">
              <Globe className="w-4 h-4 text-blue-600" /> {page.name || `Slot ${page.key}`}
              <Badge variant="outline" className="text-[10px] uppercase">{page.key}</Badge>
              {!isActive && <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300 bg-amber-50">Inactiva</Badge>}
            </CardTitle>
            {page.updated_at && (
              <p className="text-[11px] text-slate-400 mt-0.5">
                Última edición: {new Date(page.updated_at).toLocaleString("es-MX")}
                {page.updated_by && ` · ${page.updated_by}`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor={`active-${page.key}`} className="text-xs text-slate-600 cursor-pointer">Activa</Label>
            <Switch
              id={`active-${page.key}`}
              checked={isActive}
              onCheckedChange={(v) => { setIsActive(v); setDirty(true); }}
              data-testid={`active-toggle-${page.key}`}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        <div>
          <Label className="text-xs">Nombre interno (no se muestra al público)</Label>
          <Input
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            placeholder="Ej. Landing Promoción Verano 2026"
            data-testid={`name-input-${page.key}`}
          />
        </div>

        <div>
          <Label className="text-xs">Enlace público</Label>
          <div className="flex gap-2 items-center mt-1">
            <code className="flex-1 text-xs bg-slate-50 border border-slate-200 px-3 py-2 rounded truncate" data-testid={`url-${page.key}`}>
              {publicUrl}
            </code>
            <Button size="sm" variant="outline" onClick={() => copyText(publicUrl)} title="Copiar enlace" data-testid={`copy-url-${page.key}`}>
              <Copy className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => window.open(publicUrl, "_blank")} title="Abrir enlace" data-testid={`open-url-${page.key}`}>
              <ExternalLink className="w-3.5 h-3.5" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={rotateSlug}
              disabled={rotating}
              title="Generar nuevo enlace (invalida el anterior)"
              className="text-amber-700 hover:bg-amber-50 border-amber-300"
              data-testid={`rotate-url-${page.key}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${rotating ? "animate-spin" : ""}`} />
              <span className="ml-1 hidden sm:inline">Cambiar link</span>
            </Button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs flex items-center gap-1">
              <Code2 className="w-3 h-3" /> Código HTML / JS
            </Label>
            <Button size="sm" variant="ghost" onClick={() => setShowHtml(!showHtml)} className="text-xs h-7" data-testid={`toggle-html-${page.key}`}>
              {showHtml ? <><EyeOff className="w-3 h-3 mr-1" /> Ocultar</> : <><Eye className="w-3 h-3 mr-1" /> Mostrar</>}
            </Button>
          </div>
          {showHtml && (
            <textarea
              value={html}
              onChange={(e) => { setHtml(e.target.value); setDirty(true); }}
              placeholder="<!DOCTYPE html>&#10;<html>&#10;  ..."
              className="w-full h-[420px] font-mono text-xs p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              data-testid={`html-editor-${page.key}`}
            />
          )}
          {!showHtml && (
            <p className="text-xs text-slate-400 italic">
              {html ? `${html.length.toLocaleString("es-MX")} caracteres de código guardados.` : "Sin contenido HTML aún."}
            </p>
          )}
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={!dirty || saving} className="bg-blue-600 hover:bg-blue-700" data-testid={`save-${page.key}`}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PaginasPublicas() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPages = async () => {
    try {
      const res = await axios.get(`${API}/public-pages/admin/list`, { withCredentials: true });
      setPages(res.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar páginas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPages(); }, []);

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="public-pages-admin">
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Globe className="w-6 h-6 text-blue-600" /> Páginas públicas
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            3 enlaces externos sin login. Pega el código HTML/JS de cada página y comparte el enlace con quien necesites.
            Usa <strong>"Cambiar link"</strong> para invalidar el enlace anterior (útil al cambiar de proyecto/campaña).
          </p>
        </div>
        {loading ? (
          <p className="text-slate-400 text-sm">Cargando...</p>
        ) : (
          <div className="space-y-5">
            {pages.map((p) => (
              <PageSlotCard key={p.id} page={p} onRefresh={fetchPages} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
